import markBronze from '@/assets/design_elements/svg/mark/citizenly-mark-bronze.svg';
import markCreamOnBronze from '@/assets/design_elements/svg/mark/citizenly-mark-cream-on-bronze.svg';
import wordmarkBronze from '@/assets/design_elements/svg/wordmark/citizenly-wordmark-bronze.svg';
import wordmarkCreamOnBronze from '@/assets/design_elements/svg/wordmark/citizenly-wordmark-cream-on-bronze.svg';
import stackedDefault from '@/assets/design_elements/svg/lockup-stacked/citizenly-stacked.svg';
import stackedReverse from '@/assets/design_elements/svg/lockup-stacked/citizenly-stacked-reverse.svg';

export type LogoVariant = 'mark' | 'wordmark' | 'horizontal' | 'stacked';
export type LogoTone = 'default' | 'reverse';
export type LogoSize = 'sm' | 'md' | 'lg';

interface LogoProps {
  variant?: LogoVariant;
  tone?: LogoTone;
  size?: LogoSize;
  className?: string;
}

const MARK_SRC: Record<LogoTone, string> = {
  default: markBronze,
  reverse: markCreamOnBronze,
};
const WORDMARK_SRC: Record<LogoTone, string> = {
  default: wordmarkBronze,
  reverse: wordmarkCreamOnBronze,
};
const MARK_SIZE: Record<LogoSize, string> = {
  sm: 'h-7',
  md: 'h-9',
  lg: 'h-12',
};
const WORDMARK_SIZE: Record<LogoSize, string> = {
  sm: 'h-5',
  md: 'h-7',
  lg: 'h-9',
};
const HORIZONTAL = {
  sm: { mark: 'h-7', wordmark: 'h-5', gap: 'gap-2' },
  md: { mark: 'h-9', wordmark: 'h-6', gap: 'gap-2.5' },
  lg: { mark: 'h-12', wordmark: 'h-8', gap: 'gap-3' },
} as const;
const STACKED_SRC: Record<LogoTone, string> = {
  default: stackedDefault,
  reverse: stackedReverse,
};
const STACKED_SIZE: Record<LogoSize, string> = {
  sm: 'h-20',
  md: 'h-28',
  lg: 'h-36',
};

export default function Logo({
  variant = 'horizontal',
  tone = 'default',
  size = 'md',
  className = '',
}: LogoProps) {
  if (variant === 'mark') {
    return (
      <img
        src={MARK_SRC[tone]}
        alt="Citizenly"
        className={`${MARK_SIZE[size]} w-auto ${className}`.trim()}
      />
    );
  }

  if (variant === 'wordmark') {
    return (
      <img
        src={WORDMARK_SRC[tone]}
        alt="Citizenly"
        className={`${WORDMARK_SIZE[size]} w-auto ${className}`.trim()}
      />
    );
  }

  if (variant === 'stacked') {
    return (
      <img
        src={STACKED_SRC[tone]}
        alt="Citizenly"
        className={`${STACKED_SIZE[size]} w-auto ${className}`.trim()}
      />
    );
  }

  // 'horizontal' — composed for pixel-perfect vertical alignment.
  const proportions = HORIZONTAL[size];
  return (
    <span
      role="img"
      aria-label="Citizenly"
      className={`inline-flex items-center ${proportions.gap} ${className}`.trim()}
    >
      <img
        src={MARK_SRC[tone]}
        alt=""
        aria-hidden="true"
        className={`${proportions.mark} w-auto`}
      />
      <img
        src={WORDMARK_SRC[tone]}
        alt=""
        aria-hidden="true"
        className={`${proportions.wordmark} w-auto`}
      />
    </span>
  );
}
